import { type JSX } from "react";

import { Link } from "react-router";

import { cn } from "@/lib/utils";

interface SectionNavEntry {
  /** Stable React key for the entry. */
  readonly id: string;
  readonly label: string;
  /** Whether this entry is the section on screen. */
  readonly active: boolean;
}

/** An entry that is its own route, or a query on one: rendered as a link. */
interface SectionNavLink extends SectionNavEntry {
  readonly to: string;
  readonly onSelect?: never;
}

/** An entry that switches local state on the same URL: rendered as a button. */
interface SectionNavButton extends SectionNavEntry {
  readonly onSelect: () => void;
  readonly to?: never;
}

export type SectionNavItem = SectionNavLink | SectionNavButton;

export interface SectionNavProps {
  /**
   * `primary` is the horizontal tab bar that belongs to a resource (agent,
   * project); `secondary` is the vertical section list inside one page
   * (Configuration, Connections), visibly subordinate to it.
   */
  readonly variant: "primary" | "secondary";
  /** Accessible name of the `nav` landmark. */
  readonly label: string;
  readonly items: readonly SectionNavItem[];
  readonly className?: string;
}

/**
 * #163 R4: 13px / 500 in the foreground at rest, so a primary tab never reads
 * as muted secondary text; the current tab adds weight 600 and a 2px brand
 * underline that sits on the bar's own hairline (`-mb-px` on the item).
 */
const PRIMARY_ITEM =
  "block border-b-2 border-transparent py-2.5 text-sm font-medium whitespace-nowrap text-foreground no-underline hover:border-border aria-[current=page]:border-brand aria-[current=page]:font-semibold aria-[current=page]:hover:border-brand";

/**
 * The vertical list Settings already uses: 13px, muted until current, and the
 * current entry marked by an accent fill, weight and a brand bar at its start
 * edge — never by colour alone.
 */
const SECONDARY_ITEM =
  "block w-full border-l-2 border-transparent px-2 py-1.5 text-left text-sm text-muted-foreground no-underline hover:bg-accent/50 hover:text-foreground aria-[current=page]:border-brand aria-[current=page]:bg-accent aria-[current=page]:font-medium aria-[current=page]:text-accent-foreground";

function isActive(item: SectionNavItem): boolean {
  return item.active;
}

/**
 * The one section navigation for agent tabs, project tabs and the
 * Configuration and Connections section lists (#163 AC6), replacing four
 * hand-rolled bars that each spaced and coloured themselves.
 *
 * Callers state which entry is current; the component only guarantees that at
 * most one carries `aria-current="page"` — the first active entry — so two
 * overlapping matches can never announce two current pages.
 */
export function SectionNav({ variant, label, items, className }: SectionNavProps): JSX.Element {
  const current = items.findIndex(isActive);
  const itemClass = variant === "primary" ? PRIMARY_ITEM : SECONDARY_ITEM;
  return (
    <nav aria-label={label} className={className} data-variant={variant}>
      <ul
        className={
          variant === "primary" ? "flex flex-wrap gap-x-5 border-b" : "grid content-start gap-0.5"
        }
      >
        {items.map(function entry(item, index): JSX.Element {
          const ariaCurrent = index === current ? "page" : undefined;
          return (
            <li className={cn(variant === "primary" && "-mb-px")} key={item.id}>
              {item.to === undefined ? (
                <button
                  aria-current={ariaCurrent}
                  className={itemClass}
                  onClick={item.onSelect}
                  type="button"
                >
                  {item.label}
                </button>
              ) : (
                <Link aria-current={ariaCurrent} className={itemClass} to={item.to}>
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
